import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signUp, setUserRole as setUserRoleAuth } from "../../supabase/auth";
import { supabase } from "../../supabase/supabaseClient";
import AuthThemeToggle from "./AuthThemeToggle";
import "./Auth.css";
import "./SignUp.css";

const POSITION_OPTIONS = [
    { value: "Goalkeeper", short: "GK" },
    { value: "Left Back", short: "LB" },
    { value: "Centre Back", short: "CB" },
    { value: "Right Back", short: "RB" },
    { value: "Defensive Midfield", short: "CDM" },
    { value: "Central Midfield", short: "CM" },
    { value: "Attacking Midfield", short: "AM" },
    { value: "Right Wing", short: "RW" },
    { value: "Left Wing", short: "LW" },
    { value: "Center Forward", short: "CF" },
];

function SignUp() {
    const navigate = useNavigate();

    // Step 1: Email & password
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    // Step 2: User profile (first/last name - for everyone)
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");

    // Step 3: Role selection (player / scout / coach)
    const [userRole, setUserRole] = useState("player");

    // Step 4+: Player-specific fields
    const [positions, setPositions] = useState([]);
    const [dateOfBirth, setDateOfBirth] = useState("");
    const [city, setCity] = useState("");
    const [citySearch, setCitySearch] = useState("");
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [cityLoading, setCityLoading] = useState(false);
    const [height, setHeight] = useState("");
    const [weight, setWeight] = useState("");
    const [bio, setBio] = useState("");
    const [showCityDropdown, setShowCityDropdown] = useState(false);

    // Form state
    const [step, setStep] = useState(1); // 1: email/pwd, 2: user profile, 3: role, 4/5: player details
    const [status, setStatus] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);

    const fetchCities = async (query) => {
        if (query.length < 2) {
            setCitySuggestions([]);
            return;
        }

        setCityLoading(true);
        try {
            const response = await fetch(
                `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&country=Canada&count=4&language=en`
            );
            const data = await response.json();

            if (data.results) {
                const cities = data.results.map((r) => ({
                    name: r.name,
                    admin1: r.admin1 || "",
                    country: r.country,
                }));
                setCitySuggestions(cities);
            } else {
                setCitySuggestions([]);
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
            setCitySuggestions([]);
        } finally {
            setCityLoading(false);
        }
    };

    const handleCitySearch = (value) => {
        setCitySearch(value);
        setShowCityDropdown(true);

        // Debounce API calls
        if (searchTimeout) clearTimeout(searchTimeout);
        const timeout = setTimeout(() => {
            fetchCities(value);
        }, 300);
        setSearchTimeout(timeout);
    };

    const togglePosition = (pos) => {
        setPositions((prev) => {
            if (prev.includes(pos)) {
                return prev.filter((p) => p !== pos);
            }

            if (prev.length >= 3) {
                return prev; // cap at 3 positions
            }

            return [...prev, pos];
        });
    };

    const createAuthAccount = async () => {
        const cleanEmail = email.trim().toLowerCase();
        const { data, error } = await signUp(cleanEmail, password);
        if (error) throw error;
        const user = data?.user;
        if (!user) throw new Error("Account created but no user was returned.");
        return user;
    };

    // Step 1: Collect credentials only. Account is created on final submit.
    const handleStep1Submit = async (e) => {
        e.preventDefault();
        setStatus("");

        if (!email || !password) {
            setStatus("Email and password required");
            return;
        }

        if (password.length < 6) {
            setStatus("Password must be at least 6 characters");
            return;
        }

        setStep(2);
    };

    // Step 2: Create user_profile (for everyone)
    const handleStep2Submit = async (e) => {
        e.preventDefault();
        setStatus("");

        if (!firstName || !lastName) {
            setStatus("First and last name required");
            return;
        }

        try {
            // Keep names locally for now; don't write to the DB until role is chosen.
            setStatus("");
            setStep(3);
        } catch (error) {
            setStatus(error.message || "Error creating profile");
            console.error("Profile creation error:", error);
        }
    };

    // Step 3: Select role
    const handleStep3Submit = (e) => {
        e.preventDefault();

        if (userRole === "player") {
            // Player: continue to step 4 (positions/DOB/city)

            setStep(4);
        } else {
            // Org user (scout/coach): set role and go to org onboarding
            handleOrgUserRoute();
        }
    };

    // Route org users to onboarding
    const handleOrgUserRoute = async () => {
        try {
            setSubmitting(true);
            setStatus("Creating your account...");
            const user = await createAuthAccount();

            // Update auth metadata to reflect org role, create/update user_profiles, then navigate to org onboarding.
            const normalizedRole = userRole ? userRole.toString().toLowerCase() : userRole;
            await setUserRoleAuth(normalizedRole);
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) console.warn("Session refresh warning:", refreshError);

            // Create or update the user's profile now that role is known
            try {
                const { error: upsertErr } = await supabase
                    .from("user_profiles")
                    .upsert([
                        {
                            user_id: user.id,
                            first_name: firstName,
                            last_name: lastName,
                            role: normalizedRole,
                            city: city || null,
                        },
                    ]);
                if (upsertErr) console.warn("Could not upsert user_profiles:", upsertErr.message);
            } catch (e) {
                console.warn("Error upserting user_profiles:", e);
            }

            navigate("/onboarding/org", {
                state: { userId: user.id, orgRole: normalizedRole },
            });
        } catch (error) {
            setStatus(error.message || "Error setting user role / creating user profile");
            console.error("Role error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    // Step 4: Player details (positions, DOB, city, team)
    const handleStep4Submit = async (e) => {
        e.preventDefault();
        setStatus("");

        if (!positions.length || !dateOfBirth || !city) {
            setStatus("All fields required");
            return;
        }

        setStep(5);
    };

    // Step 5: Player bio (height, weight, bio) - create player_profile
    const handleStep5Submit = async (e) => {
        e.preventDefault();
        setStatus("");

        try {
            setSubmitting(true);
            setStatus("Completing setup...");
            const user = await createAuthAccount();

            // Set role to player (update auth metadata)
            await setUserRoleAuth("player");

            // Keep user_profiles in sync so name and role are available globally.
            const { error: userProfileError } = await supabase.from("user_profiles").upsert([
                {
                    user_id: user.id,
                    first_name: firstName,
                    last_name: lastName,
                    role: "player",
                    city: city || null,
                },
            ]);
            if (userProfileError) throw userProfileError;

            // Create player_profile
            const { error } = await supabase.from("player_profiles").insert([
                {
                    firstname: firstName,
                    lastname: lastName,
                    user_id: user.id,
                    positions: positions,
                    DateOfBirth: dateOfBirth,
                    city: city,
                    height: height ? parseInt(height) : null,
                    weight: weight ? parseInt(weight) : null,
                    bio: bio,
                },
            ]);

            if (error) throw error;

            setStatus("");
            navigate("/profile");
        } catch (error) {
            setStatus(error.message || "Error completing signup");
            console.error("Final signup error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (step === 1) {
            await handleStep1Submit(e);
        } else if (step === 2) {
            await handleStep2Submit(e);
        } else if (step === 3) {
            handleStep3Submit(e);
        } else if (step === 4) {
            await handleStep4Submit(e);
        } else if (step === 5) {
            await handleStep5Submit(e);
        }
    };

    // Compute single button label
    const buttonLabel = (() => {
        if (step === 1) return "Continue";
        if (step === 2) return "Next";
        if (step === 3) return userRole === "player" ? "Next" : "Continue to Organization Setup";
        if (step === 4) return "Next";
        if (step === 5) return "Create Account";
        return "Continue";
    })();

    const totalSteps = userRole === "player" ? 5 : 3;
    const visibleStep = Math.min(step, totalSteps);

    const handleBack = () => {
        setStatus("");
        setStep((prev) => {
            if (prev <= 1) return 1;
            return prev - 1;
        });
    };

    return (
        <main className="auth-page">
            <AuthThemeToggle />
            <div className="auth-shell">
                <section className="auth-left-panel">
                    <div className="auth-left-overlay" />
                    <div className="auth-left-content">
                        <img
                            src="/DribblLogo.jpg"
                            alt="Dribbl logo"
                            className="auth-left-logo"
                        />
                        <h1 className="auth-left-title">
                            The platform built for the game.
                        </h1>
                        <p className="auth-left-text">
                            Build your profile, connect with people in football, and get discovered.
                        </p>
                    </div>
                </section>

                <section className="auth-right-panel">
                    <section className="auth-card auth-card-signup">
                        <div className="brand-header">
                            <div className="brand-logo-row" />
                            <p className="brand-tagline">Be a part of Canadian History</p>
                        </div>

                        <div className="auth-view-switch" role="tablist" aria-label="Authentication views">
                            <Link to="/login" className="auth-view-tab" role="tab" aria-selected="false">Log In</Link>
                            <Link to="/signup" className="auth-view-tab auth-view-tab-active" role="tab" aria-selected="true">Sign Up</Link>
                        </div>

                        <h1>Join Dribbl</h1>
                        <p className="auth-subtitle">Create your profile</p>
                        <p className="auth-progress">Step {visibleStep} of {totalSteps}</p>
                        <form className="form auth-form" onSubmit={handleSubmit}>
                            {step === 1 && (
                                <>
                                    <label>
                                        Email
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                    </label>

                                    <label>
                                        Password
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                    </label>
                                    <p className="auth-subtext">Use at least 6 characters.</p>
                                </>
                            )}

                    {step === 2 && (
                        <>
                            <label>
                                First name
                                <input
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                />
                            </label>
                            <label>
                                Last name
                                <input
                                    type="text"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                />
                            </label>
                            <p className="auth-subtext">
                                This name will be visible on your profile.
                            </p>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <fieldset className="segmented-toggle">
                                <legend>What best describes you?</legend>
                                <label>
                                    <input
                                        type="radio"
                                        name="userRole"
                                        value="player"
                                        checked={userRole === "player"}
                                        onChange={() => setUserRole("player")}
                                    />
                                    <span>Player</span>
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="userRole"
                                        value="scout"
                                        checked={userRole === "scout"}
                                        onChange={() => setUserRole("scout")}
                                    />
                                    <span>Scout</span>
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="userRole"
                                        value="coach"
                                        checked={userRole === "coach"}
                                        onChange={() => setUserRole("coach")}
                                    />
                                    <span>Coach</span>
                                </label>
                            </fieldset>
                            <p className="auth-subtext">
                                Scouts and coaches will set up or join an organization next.
                            </p>
                        </>
                    )}

                    {step === 4 && (
                        <div className="field-group">
                            <fieldset className="positions-grid">
                                <legend>Position(s) *</legend>
                                <div className="positions-grid-inner">
                                    {POSITION_OPTIONS.map(({ value, short }) => (
                                        <button
                                            type="button"
                                            key={value}
                                            className={`position-pill ${positions.includes(value)
                                                ? "position-pill-selected"
                                                : ""
                                                }`}
                                            onClick={() => togglePosition(value)}
                                        >
                                            <span className="position-short">{short}</span>
                                        </button>
                                    ))}
                                </div>
                            </fieldset>
                            <p className="positions-summary">
                                {positions.length
                                    ? `Selected: ${positions.join(", ")} (max 3)`
                                    : "No positions selected yet"}
                            </p>

                            <label>
                                Date of birth *
                                <input
                                    type="date"
                                    value={dateOfBirth}
                                    onChange={(e) => setDateOfBirth(e.target.value)}
                                    min="1900-01-01"
                                    max="9999-12-31"
                                    required
                                />
                            </label>

                            <label>
                                City *
                                <input
                                    type="text"
                                    value={citySearch}
                                    onChange={(e) => handleCitySearch(e.target.value)}
                                    onFocus={() => setShowCityDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                                    placeholder="Search for a city..."
                                    required
                                />
                                {showCityDropdown && (
                                    <div className="city-dropdown">
                                        {cityLoading && <div className="city-option-loading">Searching...</div>}
                                        {!cityLoading && citySuggestions.length > 0 && (
                                            citySuggestions.map((suggestion, idx) => (
                                                <div
                                                    key={idx}
                                                    className="city-option"
                                                    onClick={() => {
                                                        setCity(
                                                            suggestion.admin1
                                                                ? `${suggestion.name}, ${suggestion.admin1}`
                                                                : suggestion.name
                                                        );
                                                        setCitySearch(
                                                            suggestion.admin1
                                                                ? `${suggestion.name}, ${suggestion.admin1}`
                                                                : suggestion.name
                                                        );
                                                        setShowCityDropdown(false);
                                                    }}
                                                >
                                                    <div className="city-name">{suggestion.name}</div>
                                                    {suggestion.admin1 && (
                                                        <div className="city-province">{suggestion.admin1}</div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                        {!cityLoading &&
                                            citySearch.length > 1 &&
                                            citySuggestions.length === 0 && (
                                                <div className="city-option-empty">No cities found</div>
                                            )}
                                    </div>
                                )}
                            </label>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="field-group">
                            <label>
                                Height (cm)
                                <input
                                    type="number"
                                    value={height}
                                    onChange={(e) => setHeight(e.target.value)}
                                    placeholder="e.g., 175"
                                    step="0.1"
                                    min="0"
                                />
                            </label>
                            <label>
                                Weight (kg)
                                <input
                                    type="number"
                                    value={weight}
                                    onChange={(e) => setWeight(e.target.value)}
                                    placeholder="e.g., 70"
                                    step="0.1"
                                    min="0"
                                />
                            </label>
                            <label>
                                Bio (optional)
                                <textarea
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    placeholder="Tell us about yourself, your playing style, achievements, etc."
                                    rows="4"
                                />
                            </label>
                        </div>
                    )}

                            <div className="auth-actions-row">
                                {step > 1 && (
                                    <button type="button" className="auth-btn-secondary" onClick={handleBack} disabled={submitting}>
                                        Back
                                    </button>
                                )}
                                <button type="submit" disabled={submitting}>{submitting ? "Please wait..." : buttonLabel}</button>
                            </div>
                        </form>
                        {status && <p className="status-text">{status}</p>}

                        <p className="auth-subtext">
                            Already have an account? <Link to="/login">Log in</Link>
                        </p>
                    </section>
                </section>
            </div>
        </main>
    );
}

export default SignUp;
